import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Typography,
  Table,
  notification,
  Tag,
} from 'antd';
import { UserOutlined, ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import { formatDateInTimezone } from '../lib/timezones';
import { useCurrency } from '../lib/useCurrency';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const { Text } = Typography;
const formatTimezoneLabel = (timezone) => {
  if (!timezone) return 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const normalized = tzPart.replace('GMT', 'UTC');
    return normalized ? `${timezone} (${normalized})` : timezone;
  } catch {
    return timezone;
  }
};

function UserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const ordersPageSize = 6;
  const { formatFromEur, formatAmount } = useCurrency();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const userRes = await axios.get(`${API_URL}/api/users/${id}`, config);
        setUser(userRes.data);

        const ordRes = await axios.get(`${API_URL}/api/orders/user/${id}`, config);
        setOrders(ordRes.data.data || ordRes.data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          navigate('/admin?tab=users');
          return;
        }
        notification.error({ message: 'Failed to load user data' });
        navigate('/admin');
      }
    };
    fetchData();
  }, [id, navigate]);

  const orderColumns = useMemo(
    () => [
      { title: 'Order Number', dataIndex: 'order_number', key: 'order_number' },
      { title: 'Paynet Order ID', key: 'paynet', render: (_, record) => record?.payment?.payNetOrderId || '-' },
      { title: 'Resource', dataIndex: ['Resource', 'name'], key: 'resource' },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <Tag color={status === 'active' ? 'green' : status === 'completed' ? 'blue' : status === 'cancelled' ? 'red' : 'orange'}>
            {status}
          </Tag>
        ),
      },
      {
        title: 'Payment',
        dataIndex: 'payment_status',
        key: 'payment_status',
        render: (paymentStatus) => {
          const normalized = paymentStatus === 'pending'
            ? 'payment_awaited'
            : paymentStatus === 'failed'
            ? 'payment_failed'
            : paymentStatus;
          const label = normalized === 'paid'
            ? 'Paid'
            : normalized === 'payment_failed'
            ? 'Payment failed'
            : 'Payment awaited';
          return (
            <Tag color={normalized === 'paid' ? 'green' : normalized === 'payment_failed' ? 'red' : 'orange'}>
              {label}
            </Tag>
          );
        },
      },
      {
        title: 'Start Time',
        dataIndex: 'start_time',
        key: 'start_time',
        render: (text) => formatDateInTimezone(text, user?.timezone || 'UTC'),
      },
      {
        title: 'End Time',
        dataIndex: 'end_time',
        key: 'end_time',
        render: (text) => formatDateInTimezone(text, user?.timezone || 'UTC'),
      },
      {
        title: 'Cost',
        dataIndex: 'total_cost',
        key: 'total_cost',
        render: (text, record) => {
          const currency = record?.currency || record?.order_currency;
          return currency ? formatAmount(Number(text || 0), currency) : formatFromEur(Number(text || 0));
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Button
            icon={<FileTextOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/order/${record.order_number}`);
            }}
          >
            Open Order
          </Button>
        ),
      },
    ],
    [navigate, user?.timezone, formatAmount, formatFromEur],
  );

  const pagedOrders = useMemo(() => orders.slice(
    (ordersPage - 1) * ordersPageSize,
    ordersPage * ordersPageSize,
  ), [orders, ordersPage, ordersPageSize]);

  if (!user) return (
    <div className="admin-user-page workspace-page admin-panel">
      <div className="workspace-backdrop"></div>
      <div className="container">
        <div className="glass userpage-card">Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="admin-user-page workspace-page admin-panel">
      <div className="workspace-backdrop"></div>
      <div className="container">
        <div className="admin-user-header">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')} className="resource-btn-ghost">
            Back to Admin
          </Button>
        </div>

        <div className="glass userpage-card">
          <div className="userpage-title">User Details</div>
          <div className="userpage-header">
            <div className="userpage-avatar">
              <UserOutlined />
            </div>
            <div className="userpage-info">
              <div className="userpage-name">
                {user.firstName || '-'} {user.lastName || '-'}
              </div>
              <div className="userpage-meta">
                <span className="userpage-meta-item">ID: {user.id}</span>
                <span className="userpage-meta-item">Email: {user.email || '-'}</span>
                <span className="userpage-meta-item">
                  Role: <Tag color={user.role === 'admin' ? 'red' : 'blue'}>{user.role}</Tag>
                </span>
                <span className="userpage-meta-item">Timezone: {formatTimezoneLabel(user.timezone || 'UTC')}</span>
              </div>
            </div>
          </div>

          <div className="userpage-details">
            <div className="userpage-detail">
              <span className="userpage-label">First Name</span>
              <span className="userpage-value">{user.firstName || '-'}</span>
            </div>
            <div className="userpage-detail">
              <span className="userpage-label">Last Name</span>
              <span className="userpage-value">{user.lastName || '-'}</span>
            </div>
          </div>
        </div>

        <div className="userpage-orders">
          <div className="userpage-title">Orders</div>
          <Table
            columns={orderColumns}
            dataSource={orders}
            rowKey="id"
            className="custom-table admin-user-orders-table"
            pagination={{
              pageSize: ordersPageSize,
              current: ordersPage,
              onChange: (page) => setOrdersPage(page),
              showSizeChanger: false,
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/admin/order/${record.order_number}`),
              style: { cursor: 'pointer' },
            })}
          />

          <div className="userpage-order-cards">
            {pagedOrders.map((order) => (
              <div
                key={order.id || order.order_number}
                className="admin-order-card glass"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/order/${order.order_number}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/admin/order/${order.order_number}`);
                }}
              >
                <div className="admin-order-card-head">
                  <div>
                    <Text strong className="admin-order-card-title">{order.order_number}</Text>
                    <div className="admin-order-card-meta">{order?.Resource?.name || '-'}</div>
                  </div>
                  <Tag color={order.status === 'active' ? 'green' : order.status === 'completed' ? 'blue' : order.status === 'cancelled' ? 'red' : 'orange'}>
                    {order.status}
                  </Tag>
                </div>
                <div className="admin-order-card-body">
                  <div className="admin-order-card-line">
                    <span className="admin-order-card-label">Start</span>
                    <span className="admin-order-card-value">{formatDateInTimezone(order.start_time, user?.timezone || 'UTC')}</span>
                  </div>
                  <div className="admin-order-card-line">
                    <span className="admin-order-card-label">End</span>
                    <span className="admin-order-card-value">{formatDateInTimezone(order.end_time, user?.timezone || 'UTC')}</span>
                  </div>
                  <div className="admin-order-card-line">
                    <span className="admin-order-card-label">Payment</span>
                    <span className="admin-order-card-value">
                      <Tag color={(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid' ? 'green' : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed' ? 'red' : 'orange'}>
                        {(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid'
                          ? 'Paid'
                          : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed'
                          ? 'Payment failed'
                          : 'Payment awaited'}
                      </Tag>
                    </span>
                  </div>
                  <div className="admin-order-card-line">
                    <span className="admin-order-card-label">Cost</span>
                    <span className="admin-order-card-value">
                      {order?.currency || order?.order_currency
                        ? formatAmount(Number(order.total_cost || 0), order.currency || order.order_currency)
                        : formatFromEur(Number(order.total_cost || 0))}
                    </span>
                  </div>
                  <div className="admin-order-card-line">
                    <span className="admin-order-card-label">Paynet Order ID</span>
                    <span className="admin-order-card-value">{order?.payment?.payNetOrderId || '-'}</span>
                  </div>
                </div>
                <div className="admin-order-card-actions">
                  <Button
                    icon={<FileTextOutlined />}
                    size="small"
                    className="admin-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/order/${order.order_number}`);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {orders.length > ordersPageSize && (
            <div className="resource-pagination">
              <button
                className="orders-page-btn"
                onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                disabled={ordersPage === 1}
                aria-label="Previous page"
              >
                {'<'}
              </button>
              <span className="orders-page-info">
                {ordersPage} / {Math.max(1, Math.ceil(orders.length / ordersPageSize))}
              </span>
              <button
                className="orders-page-btn"
                onClick={() => setOrdersPage((p) => Math.min(Math.ceil(orders.length / ordersPageSize), p + 1))}
                disabled={ordersPage >= Math.ceil(orders.length / ordersPageSize)}
                aria-label="Next page"
              >
                {'>'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserPage;


